import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form, ListGroup } from 'react-bootstrap';
import {
  LOGGED_USER_COMPARE_FIELDS,
  encodeLoggedUserCompareValue,
  getLoggedUserMergeAutocomplete,
  filterLoggedUserFieldSuggestions,
  applyLoggedUserMergeCompletion,
} from '../../utils/filterFieldValueUtils';

/**
 * Text input that suggests Logged User merge fields when the user types
 * `{{LoggedUser.` (legacy `{{CurrentUser.` still autocompletes).
 */
export default function LoggedUserMergeTextInput({
  value = '',
  onChange,
  onBlur,
  disabled = false,
  type = 'text',
  placeholder,
  style,
  className,
  isInvalid = false,
  'aria-label': ariaLabel,
  enableSuggestions = true,
}) {
  const inputRef = useRef(null);
  const caretRef = useRef(String(value ?? '').length);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const [open, setOpen] = useState(false);

  const syncCaretFromInput = () => {
    const el = inputRef.current;
    if (el && typeof el.selectionStart === 'number') {
      caretRef.current = el.selectionStart;
    }
  };

  const autocompleteCtx = useMemo(() => {
    if (!enableSuggestions) return null;
    return getLoggedUserMergeAutocomplete(value, caretRef.current);
  }, [value, enableSuggestions, open]);

  const suggestions = useMemo(() => {
    if (!autocompleteCtx) return [];
    return filterLoggedUserFieldSuggestions(autocompleteCtx.partial);
  }, [autocompleteCtx]);

  useEffect(() => {
    const shouldOpen = !!autocompleteCtx && suggestions.length > 0;
    setOpen(shouldOpen);
    if (shouldOpen) setHighlightIdx(0);
  }, [autocompleteCtx, suggestions.length]);

  const commitSuggestion = (fieldName) => {
    const { value: newValue, caret } = applyLoggedUserMergeCompletion(
      value,
      caretRef.current,
      fieldName
    );
    onChange?.(newValue);
    setOpen(false);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(caret, caret);
      caretRef.current = caret;
    });
  };

  const handleChange = (e) => {
    caretRef.current = e.target.selectionStart ?? e.target.value.length;
    onChange?.(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (!open || !suggestions.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      commitSuggestion(suggestions[highlightIdx].field_name);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  const handleBlur = (e) => {
    setTimeout(() => setOpen(false), 160);
    onBlur?.(e);
  };

  return (
    <div className="position-relative w-100">
      <Form.Control
        ref={inputRef}
        type={type}
        value={value ?? ''}
        onChange={handleChange}
        onSelect={syncCaretFromInput}
        onClick={syncCaretFromInput}
        onKeyUp={syncCaretFromInput}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        disabled={disabled}
        placeholder={placeholder}
        style={style}
        className={className}
        isInvalid={isInvalid}
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-expanded={open}
      />
      {open && (
        <ListGroup
          className="position-absolute w-100 shadow-sm border"
          style={{
            zIndex: 1060,
            maxHeight: '240px',
            overflowY: 'auto',
            top: 'calc(100% + 2px)',
          }}
        >
          {suggestions.map((f, idx) => (
            <ListGroup.Item
              key={f.field_name}
              action
              active={idx === highlightIdx}
              className="py-2 px-2"
              onMouseDown={(ev) => {
                ev.preventDefault();
                commitSuggestion(f.field_name);
              }}
            >
              <div className="small fw-semibold">{f.field_label}</div>
              <div
                className="text-muted font-monospace"
                style={{ fontSize: '0.75rem' }}
              >
                {encodeLoggedUserCompareValue(f.field_name)}
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
    </div>
  );
}
